/* eslint-disable camelcase */
import type { Experiment, Result } from '@growthbook/growthbook'
import {
  CUSTOM_IM_ANALYTICS_ERRORS,
  GTMEcommerceItem,
  GTMEvent,
  GtmEventName,
  GTMEventType,
  GTMItemListName,
  GTMLoginType,
  GTMViewContentGroup,
  LogoutType
} from '@lib/models/gtm'
import { SaleTypeEnum } from '@lib/models/common'
import { LoginType } from '@lib/models/identification'
import { Order, PaymentMethod, WithdrawalMethod } from '@lib/models/order'
import { ProductLiteCategorized, Product, SizeType } from '@lib/models/product'
import { CartProduct } from '@lib/models/purchase'
import {
  getItemListNameTypeFromProducts,
  gtmEcommerceItemFromCartProduct,
  gtmEcommerceItemFromLiteCategorized,
  gtmEcommerceItemFromProduct,
  generatePageTitle,
  formatAmountGTM,
  getCustomHashLocation,
  getGtmPickupType,
  buildGtmFilters,
  withItemIndexes,
  getGTMUserTypeFromSegments,
  calculateGtmValueFromGtmItems,
  getGTMUserIsEmployee
} from '@lib/utils/gtmUtils'
import { logDebug, logInfo, scopedLogger } from '@lib/utils/logUtils'
import { useAppStore } from '@stores/app.store'
import { useGtmStore } from '@stores/gtm.store'
import { computed } from 'vue'

const log = scopedLogger('@composables/useGtm.ts')

export type GTMPromotionData = {
  promotionId?: string
  promotionName: string
  creativeSlot: string
  linkUrl?: string
  typePromotion: string
  product?: ProductLiteCategorized | null
}

export const useGtm = () => {
  const appStore = useAppStore()
  const gtmStore = useGtmStore()

  const restaurantCode = computed(() => appStore.config?.code ?? '')
  const currency = computed(() => appStore.config?.currencyAcronym ?? '')
  const cartTotals = computed(() => appStore.cartTotals)
  const cartProducts = computed(() => appStore.cart?.productsTotals || [])
  const hasBill = computed(() => Boolean(appStore.fiscalFields))
  const paymentMethod2 = computed(() => {
    const isMixedLoyalty = !!cartTotals.value.points && !!cartTotals.value.total
    if (isMixedLoyalty) return PaymentMethod.LOYALTY
    return undefined
  })
  const shippingTier = computed(() => {
    const saleType = appStore.cart?.saleType
    if (saleType === SaleTypeEnum.EATIN) return 'restaurant'
    if (saleType === SaleTypeEnum.TAKEOUT) return 'take_away'
    return undefined
  })
  const aopCouponsApplied = computed(() => appStore.cart?.coupons)

  const viewContent = computed(() => {
    return gtmStore.viewGTMContent
  })

  /* Create events general info */
  const eventGeneralInfo = (evType: GTMEventType): GTMEvent => {
    const event = new GTMEvent(evType)
    event.country = appStore.config?.country
    event.kiosk_number = appStore.config?.posNumber
    // view
    const content = viewContent.value
    if (content) {
      event.content_group = content.cg
      event.content_group2 = content.cg2
      event.content_group3 = content.cg3
      event.page_title = generatePageTitle(content) || undefined
      event.link_url = content.linkUrl
    }
    // override custom page location
    event.page_custom_location = getCustomHashLocation()
    // user
    const user = appStore.user
    event.is_logged = !!user
    if (user) {
      event.is_loyalty = user.loyalty
      event.loyalty_points = user.points
      event.user_id = user.mcId
      event.user_type = getGTMUserTypeFromSegments(user.imUserRaw?.segments ?? [])
      event.login_type =
        user.loginType === LoginType.IM ? GTMLoginType.IM_LOGIN : GTMLoginType.SOFT_LOGIN
      event.is_employee = getGTMUserIsEmployee(user.imUserRaw?.tags ?? [])
    }
    // ecommerce
    event.ecommerce.restaurant_code = restaurantCode.value
    return event
  }

  /* Events */

  const trackGoLogin = () => {
    const go_login = eventGeneralInfo(GtmEventName.GO_LOGIN)
    sendEvent(go_login)
  }

  const trackLogin = (method: string) => {
    const login = eventGeneralInfo(GtmEventName.LOGIN)
    login.method = method
    sendEvent(login)
  }

  const trackLoginError = ({ errorType, method }: { errorType: string; method: string }) => {
    const login_error = eventGeneralInfo(GtmEventName.LOGIN_ERROR)
    login_error.error_code = `${CUSTOM_IM_ANALYTICS_ERRORS.LOGIN_ERROR}`
    login_error.error_type = errorType
    login_error.method = method
    sendEvent(login_error)
  }

  const trackLogout = (method: string) => {
    const logout = eventGeneralInfo(GtmEventName.LOGOUT)
    logout.method = method
    sendEvent(logout)
  }

  const trackMenuButton = (button: string) => {
    const menu_button = eventGeneralInfo(GtmEventName.MENU(button))
    sendEvent(menu_button)
  }

  const trackGetBill = () => {
    const get_bill = eventGeneralInfo(GtmEventName.GET_BILL)
    sendEvent(get_bill)
  }

  const trackGoMym = () => {
    const go_mym = eventGeneralInfo(GtmEventName.GO_MYM)
    sendEvent(go_mym)
  }

  const trackMymHome = () => {
    const mym_home = eventGeneralInfo(GtmEventName.MYM_HOME)
    sendEvent(mym_home)
  }

  const trackViewItemList = ({
    items,
    itemListName,
    filters
  }: {
    items: GTMEcommerceItem[]
    itemListName?: string
    filters?: string
  }) => {
    const view_item_list = eventGeneralInfo(GtmEventName.VIEW_ITEM_LIST)
    // ecommerce
    view_item_list.ecommerce.shipping_tier = shippingTier.value
    view_item_list.ecommerce.currency = currency.value
    const sortedItems = items.toSorted((a, b) => {
      if (a.index && b.index) return a.index - b.index
      return 0
    })
    view_item_list.ecommerce.items = sortedItems
    view_item_list.ecommerce.item_list_name =
      itemListName ?? getItemListNameTypeFromProducts(sortedItems)
    view_item_list.ecommerce.loyalty =
      itemListName === GTMItemListName.LOYALTY_HOME ||
      itemListName === GTMItemListName.HOME_FEATURED_LOYALTY
    view_item_list.ecommerce.filters_applied = filters
    sendEvent(view_item_list)
  }

  const trackSelectItemCategorized = ({
    item,
    isLoyalty = false,
    itemListName,
    filters,
    index
  }: {
    item: ProductLiteCategorized
    isLoyalty?: boolean
    itemListName?: string
    filters?: string[]
    index?: number
  }) => {
    const select_item = eventGeneralInfo(GtmEventName.SELECT_ITEM)
    // ecommerce
    // Only inject the loyalty-based default when the caller didn't resolve one — everything
    // else (productType-based value, or the 'other' fallback) is left to the builder itself,
    // same as trackViewItem/trackCustomizeItem/etc., instead of overwriting it afterward.
    if (!itemListName && isLoyalty) {
      itemListName = GTMItemListName.LOYALTY_HOME
    }
    const gtmEcommerceItem = gtmEcommerceItemFromLiteCategorized({
      product: item,
      currency: currency.value,
      isLoyalty,
      itemListName
    })
    gtmEcommerceItem.index = index

    select_item.ecommerce.shipping_tier = shippingTier.value
    select_item.ecommerce.items = [gtmEcommerceItem]
    select_item.ecommerce.value = calculateGtmValueFromGtmItems([gtmEcommerceItem])
    select_item.ecommerce.currency = currency.value
    select_item.ecommerce.loyalty = isLoyalty
    select_item.ecommerce.points = gtmEcommerceItem.points
    select_item.ecommerce.item_list_name = gtmEcommerceItem.item_list_name
    select_item.ecommerce.filters_applied = buildGtmFilters(filters)
    sendEvent(select_item)
  }

  const trackViewItem = ({
    item,
    isLoyalty = false,
    aopCoupon,
    itemListName
  }: {
    item: Product
    isLoyalty?: boolean
    aopCoupon?: string
    itemListName?: string
  }) => {
    const view_item = eventGeneralInfo(GtmEventName.VIEW_ITEM)
    // ecommerce
    const gtmEcommerceItem = gtmEcommerceItemFromProduct({
      product: item,
      currency: currency.value,
      isLoyalty,
      aopCoupon,
      itemListName
    })
    gtmEcommerceItem.index = 1
    view_item.ecommerce.shipping_tier = shippingTier.value
    view_item.ecommerce.items = [gtmEcommerceItem]
    view_item.ecommerce.value = calculateGtmValueFromGtmItems([gtmEcommerceItem])
    view_item.ecommerce.currency = currency.value
    view_item.ecommerce.loyalty = isLoyalty
    view_item.ecommerce.points = gtmEcommerceItem.points
    sendEvent(view_item)
  }

  const trackCustomizeItem = (product: Product, itemListName?: string) => {
    const customize_item = eventGeneralInfo(GtmEventName.CUSTOMIZE_ITEM)

    const item = gtmEcommerceItemFromProduct({
      product,
      currency: currency.value,
      itemListName
    })
    item.index = 1

    customize_item.ecommerce.items = [item]
    sendEvent(customize_item)
  }

  const trackSelectComponent = (
    product: Product,
    componentInfo: { id: string; name: string },
    itemListName?: string
  ) => {
    const select_component = eventGeneralInfo(GtmEventName.SELECT_COMPONENT)

    const item = gtmEcommerceItemFromProduct({
      product,
      currency: currency.value,
      itemListName
    })
    item.component_id = componentInfo.id
    item.component_name = componentInfo.name
    item.index = 1

    select_component.ecommerce.items = [item]
    sendEvent(select_component)
  }

  const trackAdditionalComponent = ({ itemId, itemName }: { itemId: string; itemName: string }) => {
    const additional_component = eventGeneralInfo(GtmEventName.ADDITIONAL_COMPONENT)

    const item = new GTMEcommerceItem()
    item.item_id = itemId
    item.item_name = itemName
    item.index = 1

    additional_component.ecommerce.items = [item]
    sendEvent(additional_component)
  }

  const trackCustomizeIngredients = (
    product: Product,
    componentInfo?: { id: string; name: string },
    itemListName?: string
  ) => {
    const customize_ingredients = eventGeneralInfo(GtmEventName.CUSTOMIZE_INGREDIENTS)

    const item = gtmEcommerceItemFromProduct({
      product,
      currency: currency.value,
      itemListName
    })
    if (componentInfo) {
      item.component_id = componentInfo.id
      item.component_name = componentInfo.name
    }
    item.index = 1
    customize_ingredients.ecommerce.items = [item]
    sendEvent(customize_ingredients)
  }

  const trackSaveIngredients = (
    product: Product,
    ingredientsEdited: string,
    componentInfo?: { id: string; name: string },
    itemListName?: string
  ) => {
    const save_ingredients = eventGeneralInfo(GtmEventName.SAVE_INGREDIENTS)
    const item = gtmEcommerceItemFromProduct({
      product,
      currency: currency.value,
      itemListName
    })
    if (componentInfo) {
      item.component_id = componentInfo.id
      item.component_name = componentInfo.name
    }
    item.ingredients_edited = ingredientsEdited
    item.index = 1
    save_ingredients.ecommerce.items = [item]
    sendEvent(save_ingredients)
  }

  // item when adding to cart only one product
  // items when modifying units in cart
  const trackAddToCart = ({
    item,
    items,
    units,
    computedUnitPrice,
    isLoyalty,
    aopCoupon,
    itemListName
  }:
    | {
        item: Product
        units: number
        computedUnitPrice: number
        isLoyalty: boolean
        itemListName?: string
        aopCoupon?: string
        items?: never
      }
    | {
        items: CartProduct[]
        isLoyalty: boolean
        item?: never
        units?: never
        computedUnitPrice?: never
        itemListName?: never
        aopCoupon?: never
      }) => {
    const add_to_cart = eventGeneralInfo(GtmEventName.ADD_TO_CART)

    if (item) {
      const gtmEcommerceItem = gtmEcommerceItemFromLiteCategorized({
        product: item,
        currency: currency.value,
        computedUnitPrice,
        isLoyalty,
        aopCoupon,
        itemListName
      })
      gtmEcommerceItem.quantity = units
      gtmEcommerceItem.index = 1
      add_to_cart.ecommerce.items = [gtmEcommerceItem]
    } else if (items) {
      add_to_cart.ecommerce.items = withItemIndexes(
        items.map((product) =>
          gtmEcommerceItemFromCartProduct({
            cartProduct: product,
            currency: currency.value,
            aopCouponsApplied: aopCouponsApplied.value
          })
        )
      )
    }

    add_to_cart.ecommerce.value = calculateGtmValueFromGtmItems(add_to_cart.ecommerce.items ?? [])
    add_to_cart.ecommerce.shipping_tier = shippingTier.value
    add_to_cart.ecommerce.currency = currency.value
    add_to_cart.ecommerce.loyalty = isLoyalty
    add_to_cart.ecommerce.points = cartTotals.value.points
    sendEvent(add_to_cart)
  }

  const trackGoCart = () => {
    const go_cart = eventGeneralInfo(GtmEventName.GO_CART)
    sendEvent(go_cart)
  }

  const trackViewCart = () => {
    const view_cart = eventGeneralInfo(GtmEventName.VIEW_CART)

    const items = cartProducts.value.map((product) =>
      gtmEcommerceItemFromCartProduct({
        cartProduct: product,
        currency: currency.value,
        aopCouponsApplied: aopCouponsApplied.value
      })
    )
    view_cart.ecommerce.shipping_tier = shippingTier.value
    view_cart.ecommerce.currency = currency.value
    view_cart.ecommerce.items = withItemIndexes(items)
    view_cart.ecommerce.value = calculateGtmValueFromGtmItems(items)
    view_cart.ecommerce.loyalty = !!cartTotals.value.points
    view_cart.ecommerce.points = cartTotals.value.points

    sendEvent(view_cart)
  }

  const trackRemoveFromCart = (cartProducts: CartProduct[]) => {
    const remove_from_cart = eventGeneralInfo(GtmEventName.REMOVE_FROM_CART)

    const items = cartProducts.map((product) =>
      gtmEcommerceItemFromCartProduct({
        cartProduct: product,
        currency: currency.value,
        aopCouponsApplied: aopCouponsApplied.value
      })
    )
    remove_from_cart.ecommerce.shipping_tier = shippingTier.value
    remove_from_cart.ecommerce.currency = currency.value
    remove_from_cart.ecommerce.items = withItemIndexes(items)
    remove_from_cart.ecommerce.value = calculateGtmValueFromGtmItems(items)
    remove_from_cart.ecommerce.loyalty = cartProducts.some((product) => product.points)
    remove_from_cart.ecommerce.points = cartTotals.value.points

    sendEvent(remove_from_cart)
  }

  const trackApplyCoupon = () => {
    const apply_coupon = eventGeneralInfo(GtmEventName.APPLY_COUPON)
    sendEvent(apply_coupon)
  }

  const trackContinueOrdering = () => {
    const continue_ordering = eventGeneralInfo(GtmEventName.CONTINUE_ORDERING)
    sendEvent(continue_ordering)
  }

  const trackBeginCheckout = () => {
    const begin_checkout = eventGeneralInfo(GtmEventName.BEGIN_CHECKOUT)
    const items = cartProducts.value.map((product) =>
      gtmEcommerceItemFromCartProduct({
        cartProduct: product,
        currency: currency.value,
        aopCouponsApplied: aopCouponsApplied.value
      })
    )
    begin_checkout.ecommerce.shipping_tier = shippingTier.value
    begin_checkout.ecommerce.items = withItemIndexes(items)
    begin_checkout.ecommerce.value = formatAmountGTM(cartTotals.value.total)
    begin_checkout.ecommerce.currency = currency.value
    begin_checkout.ecommerce.loyalty = !!cartTotals.value.points
    begin_checkout.ecommerce.points = cartTotals.value.points
    sendEvent(begin_checkout)
  }

  const trackAddShippingInfo = () => {
    const add_shipping_info = eventGeneralInfo(GtmEventName.ADD_SHIPPING_INFO)
    const items = cartProducts.value.map((product) =>
      gtmEcommerceItemFromCartProduct({
        cartProduct: product,
        currency: currency.value,
        aopCouponsApplied: aopCouponsApplied.value
      })
    )
    add_shipping_info.ecommerce.shipping_tier = shippingTier.value
    add_shipping_info.ecommerce.items = withItemIndexes(items)
    add_shipping_info.ecommerce.value = formatAmountGTM(cartTotals.value.total)
    add_shipping_info.ecommerce.currency = currency.value
    add_shipping_info.ecommerce.shipping = 0
    add_shipping_info.ecommerce.loyalty = !!cartTotals.value.points
    add_shipping_info.ecommerce.points = cartTotals.value.points
    sendEvent(add_shipping_info)
  }

  const trackAddPaymentInfo = ({ paymentType }: { paymentType: string }) => {
    const add_payment_info = eventGeneralInfo(GtmEventName.ADD_PAYMENT_INFO)
    const items = cartProducts.value.map((product) =>
      gtmEcommerceItemFromCartProduct({
        cartProduct: product,
        currency: currency.value,
        aopCouponsApplied: aopCouponsApplied.value
      })
    )
    add_payment_info.ecommerce.shipping_tier = shippingTier.value
    add_payment_info.ecommerce.items = withItemIndexes(items)
    add_payment_info.ecommerce.value = formatAmountGTM(cartTotals.value.total)
    add_payment_info.ecommerce.currency = currency.value
    add_payment_info.ecommerce.payment_type = paymentType.toLowerCase()
    add_payment_info.ecommerce.payment_type2 = paymentMethod2.value
    add_payment_info.ecommerce.discount = formatAmountGTM(cartTotals.value.discount)
    add_payment_info.ecommerce.tax = formatAmountGTM(cartTotals.value.tax)
    add_payment_info.ecommerce.shipping = 0
    add_payment_info.ecommerce.bill = hasBill.value
    add_payment_info.ecommerce.loyalty = !!cartTotals.value.points
    add_payment_info.ecommerce.points = cartTotals.value.points
    sendEvent(add_payment_info)
  }

  const trackOrderFailed = ({
    code,
    type,
    paymentMethod
  }: {
    code: string
    type: string
    paymentMethod
  }) => {
    const order_failed = eventGeneralInfo(GtmEventName.ORDER_FAILED)
    order_failed.error_code = code
    order_failed.error_type = type.toLowerCase()
    order_failed.ecommerce.payment_type = paymentMethod.toLowerCase()
    order_failed.ecommerce.payment_type2 = paymentMethod2.value
    sendEvent(order_failed)
  }

  const trackPurchase = ({
    order,
    eventOrigin = 'adk'
  }: {
    order: Order
    eventOrigin?: string
  }) => {
    const getRemainingPointsAfterPurchase = (orderPoints: number) => {
      if (!appStore.user) return undefined
      if (!orderPoints) return appStore.user.points
      return appStore.user.points - orderPoints
    }

    const purchase = eventGeneralInfo(GtmEventName.PURCHASE)
    const items = cartProducts.value.map((product) =>
      gtmEcommerceItemFromCartProduct({
        cartProduct: product,
        currency: currency.value,
        aopCouponsApplied: aopCouponsApplied.value
      })
    )
    const orderTotals = cartTotals.value
    purchase.ecommerce.shipping_tier = shippingTier.value
    purchase.ecommerce.items = withItemIndexes(items)
    purchase.ecommerce.value = formatAmountGTM(orderTotals.total)
    purchase.ecommerce.currency = currency.value
    purchase.ecommerce.payment_type = order.paymentMethod.toLowerCase()
    purchase.ecommerce.payment_type2 = paymentMethod2.value
    purchase.ecommerce.transaction_id = order.orderId
    purchase.ecommerce.discount = formatAmountGTM(orderTotals.discount)
    purchase.ecommerce.tax = formatAmountGTM(orderTotals.tax)
    purchase.ecommerce.shipping = 0
    purchase.ecommerce.bill = hasBill.value
    const points = orderTotals.points
    purchase.ecommerce.loyalty = !!points
    purchase.ecommerce.points = points
    purchase.ecommerce.points_spent = points
    // loyalty_points has plain user points, now we need to calculate remaining points with current order
    purchase.loyalty_points = getRemainingPointsAfterPurchase(points)
    purchase.ecommerce.pickup_type = getGtmPickupType(order.area ?? WithdrawalMethod.AT_COUNTER)
    purchase.event_origin = eventOrigin
    sendEvent(purchase)
  }

  const trackSelectPickUpSelector = (selector: string) => {
    const select_pickup_selector = eventGeneralInfo(GtmEventName.SELECT_PICKUP_SELECTOR(selector))
    sendEvent(select_pickup_selector)
  }

  const trackSelectPickUp = (pickupType: string) => {
    const select_pickup = eventGeneralInfo(GtmEventName.SELECT_PICKUP)
    select_pickup.ecommerce.pickup_type = getGtmPickupType(pickupType)
    sendEvent(select_pickup)
  }

  const trackGetOnline = (coupon: string) => {
    const get_online = eventGeneralInfo(GtmEventName.GET_ONLINE)
    //ecommerce
    get_online.ecommerce.coupon = coupon
    sendEvent(get_online)
  }

  const trackCustomError = ({ code, type }: { code: string; type: string }) => {
    const custom_error = eventGeneralInfo(GtmEventName.CUSTOM_ERROR)
    custom_error.error_code = code
    custom_error.error_type = type
    sendEvent(custom_error)
  }

  const trackInactivityPopup = () => {
    const inactivity_popup = eventGeneralInfo(GtmEventName.INACTIVITY_POPUP)
    sendEvent(inactivity_popup)
  }

  const trackInactivityExit = () => {
    const inactivity_exit = eventGeneralInfo(GtmEventName.INACTIVITY_EXIT)
    sendEvent(inactivity_exit)
  }

  const trackInoperative = () => {
    const inoperative = eventGeneralInfo(GtmEventName.INOPERATIVE)
    inoperative.method = LogoutType.INOPERATIVE
    sendEvent(inoperative)
  }

  const trackSelectFilter = (filtersApplied: string) => {
    const select_filter = eventGeneralInfo(GtmEventName.SELECT_FILTER)
    select_filter.ecommerce.filters_applied = filtersApplied
    sendEvent(select_filter)
  }

  const trackSinLogin = () => {
    const sin_login = eventGeneralInfo(GtmEventName.SIN_LOGIN)
    sendEvent(sin_login)
  }

  const trackLoginUpdate = (type: string) => {
    const login_update = eventGeneralInfo(GtmEventName.LOGIN_UPDATE)
    login_update.type = type
    sendEvent(login_update)
  }

  const trackExperimentViewed = (experiment: Experiment<unknown>, result: Result<unknown>) => {
    const experiment_viewed = eventGeneralInfo(GtmEventName.EXPERIMENT_VIEWED)
    experiment_viewed.experiment_id = experiment.key
    experiment_viewed.variation_id = result.key
    sendEvent(experiment_viewed)
  }

  const trackLogoutConfirmation = () => {
    const logout_confirmation = eventGeneralInfo(GtmEventName.LOGOUT_CONFIRMATION)
    sendEvent(logout_confirmation)
  }

  const trackGoBack = () => {
    const go_back = eventGeneralInfo(GtmEventName.GO_BACK)
    sendEvent(go_back)
  }

  const trackEmptyCart = () => {
    const empty_cart = eventGeneralInfo(GtmEventName.EMPTY_CART)
    sendEvent(empty_cart)
  }

  const trackHomeButton = (buttonLabel: string) => {
    const home_button = eventGeneralInfo(GtmEventName.HOME_BUTTON(buttonLabel))
    sendEvent(home_button)
  }

  // select_promotion and view_promotion share the exact same parameters, resolved once by the
  // banner (see BlockBanner.vue). items only carries the product when the banner links to a PLU
  // (any other banner type sends []), tagged with the promotion at item level.
  const buildPromotionEvent = (eventName: GTMEventType, data: GTMPromotionData) => {
    const promotion_event = eventGeneralInfo(eventName)
    promotion_event.ecommerce.promotion_id = data.promotionId
    promotion_event.ecommerce.promotion_name = data.promotionName
    promotion_event.ecommerce.link_url = data.linkUrl
    promotion_event.ecommerce.creative_slot = data.creativeSlot
    promotion_event.ecommerce.type_promotion = data.typePromotion
    promotion_event.ecommerce.items = data.product
      ? withItemIndexes([
          {
            ...gtmEcommerceItemFromLiteCategorized({
              product: data.product,
              currency: currency.value,
              itemListName: GTMItemListName.HOME_BANNER
            }),
            promotion_id: data.promotionId,
            promotion_name: data.promotionName
          }
        ])
      : []
    return promotion_event
  }

  const trackSelectPromotion = (data: GTMPromotionData) => {
    sendEvent(buildPromotionEvent(GtmEventName.SELECT_PROMOTION, data))
  }

  const trackSelectSize = (size: SizeType) => {
    const select_size = eventGeneralInfo(GtmEventName.SELECT_SIZE)
    select_size.ecommerce.selected_size = size.toLowerCase()
    sendEvent(select_size)
  }

  const trackViewPromotion = (data: GTMPromotionData) => {
    sendEvent(buildPromotionEvent(GtmEventName.VIEW_PROMOTION, data))
  }

  const trackEmptyCartConfirmation = () => {
    const empty_cart_confirmation = eventGeneralInfo(GtmEventName.EMPTY_CART_CONFIRMATION)
    sendEvent(empty_cart_confirmation)
  }

  const trackValidationWarning = () => {
    const validation_warning = eventGeneralInfo(GtmEventName.VALIDATION_WARNING)
    sendEvent(validation_warning)
  }

  const trackValidationSummit = () => {
    const validation_summit = eventGeneralInfo(GtmEventName.VALIDATION_SUMMIT)
    sendEvent(validation_summit)
  }

  const trackSendCode = () => {
    const send_code = eventGeneralInfo(GtmEventName.SEND_CODE)
    sendEvent(send_code)
  }

  const trackClickCategory = ({categoryName, categoryIndex}: {categoryName: string, categoryIndex: number}) => {
    const click_category = eventGeneralInfo(GtmEventName.CLICK_CATEGORY)
    click_category.ecommerce.category_name = categoryName
    click_category.ecommerce.category_index = categoryIndex
    sendEvent(click_category)
  }

  const trackContinueGuest = (overridePageTitle?: string) => {
    const continue_guest = eventGeneralInfo(GtmEventName.CONTINUE_GUEST)
    if (overridePageTitle) continue_guest.page_title = overridePageTitle
    sendEvent(continue_guest)
  }

  const trackOrderExit = (method: string) => {
    const order_exit = eventGeneralInfo(GtmEventName.ORDER_EXIT)
    order_exit.method = method
    sendEvent(order_exit)
  }

  const trackPackLogoutAndExit = (method: string) => {
    if (appStore.user) trackLogout(method)
    trackOrderExit(method)
  }

  const trackInteraction = ({componentName, linkText}: {componentName: string, linkText: string}) => {
    const interactionEvent = eventGeneralInfo(GtmEventName.INTERACTION)
    interactionEvent.component_name = componentName
    interactionEvent.link_text = linkText
    sendEvent(interactionEvent)
  }

  /* PageView */
  const trackPageView = (content: GTMViewContentGroup, modal = false) => {
    gtmStore.setViewGTMContent(content, modal)
    const page_view = eventGeneralInfo(GtmEventName.PAGE_VIEW)

    sendEvent(page_view)
  }

  const trackOnCloseModal = () => {
    const previousPageViewContent = gtmStore.pageViewGTMContent
    if (previousPageViewContent) {
      trackPageView(previousPageViewContent)
    }
  }

  /* Send events method */
  const sendEvent = (ev: GTMEvent) => {
    if (globalThis) {
      if (!globalThis.dataLayer) globalThis.dataLayer = []
      globalThis.dataLayer.push(ev)
      logInfo(log, `New event pushed to datalayer [${ev.event}]`)
      logDebug(log, ev as unknown as Record<string, unknown>)
    } else logInfo(log, `Event on server, will not be sended [${ev.event}]`)
  }

  return {
    // pageView
    trackPageView,
    trackOnCloseModal,
    // events
    trackGoLogin,
    trackLogin,
    trackLoginError,
    trackLogout,
    trackMenuButton,
    trackGetBill,
    trackGoMym,
    trackMymHome,
    trackViewItemList,
    trackSelectItemCategorized,
    trackViewItem,
    trackCustomizeItem,
    trackSelectComponent,
    trackAdditionalComponent,
    trackCustomizeIngredients,
    trackSaveIngredients,
    trackAddToCart,
    trackGoCart,
    trackViewCart,
    trackRemoveFromCart,
    trackApplyCoupon,
    trackContinueOrdering,
    trackBeginCheckout,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackOrderFailed,
    trackPurchase,
    trackSelectPickUpSelector,
    trackSelectPickUp,
    trackCustomError,
    trackInactivityPopup,
    trackInactivityExit,
    trackInoperative,
    trackSelectFilter,
    trackSinLogin,
    trackLoginUpdate,
    trackExperimentViewed,
    trackLogoutConfirmation,
    trackGoBack,
    trackEmptyCart,
    trackHomeButton,
    trackSelectPromotion,
    trackGetOnline,
    trackSelectSize,
    trackViewPromotion,
    trackEmptyCartConfirmation,
    trackValidationWarning,
    trackValidationSummit,
    trackSendCode,
    trackClickCategory,
    trackContinueGuest,
    trackOrderExit,
    trackPackLogoutAndExit,
    trackInteraction
  }
}
